import React, { useState, useEffect, useMemo } from 'react';
import { AlertBand } from '@/components/AlertBand';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Calendar, Ticket } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from '@/contexts/LocationContext';
import SEO, { websiteSchema, faqSchema } from '@/components/SEO';
import { useSections } from '@/contexts/SectionsContext';
import { api } from '@/lib/api';
import { getDistanceKm, formatDistance as fmtDist } from '@/lib/distance';
import PlaceCard from '@/components/PlaceCard';
import { Skeleton } from '@/components/ui/skeleton';
import { ShareButton } from '@/components/ShareButton';

const FOOD = ['restaurants', 'gastronomy'];
const WATER = ['beaches', 'natural-pools'];

const HomePage = () => {
  const { t, getLocalizedField, language } = useLanguage();
  const { location } = useLocation();
  const { isSectionVisible } = useSections();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load data ONCE on mount - not on location change
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catsRes, placesRes, itinRes, eventsRes] = await Promise.all([
        api.getCategories(),
        api.getPlaces({ limit: 200 }),
        api.getItineraries(),
        api.getEvents({ upcoming_only: true, limit: 3 })
      ]);
      setCategories(catsRes.data);
      setNearbyPlaces(placesRes.data);
      setItineraries(itinRes.data.slice(0, 3));
      setUpcomingEvents(eventsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Compute distances client-side from live GPS - only numbers change, no data reload
  const placesWithDistance = useMemo(() => {
    if (!nearbyPlaces.length) return [];
    return nearbyPlaces.map(p => ({
      ...p,
      _distance: location?.lat ? getDistanceKm(location.lat, location.lng, p.latitude, p.longitude) : null
    })).sort((a, b) => (a._distance ?? 999) - (b._distance ?? 999));
  }, [nearbyPlaces, location]);

  const foodPlaces = useMemo(() => placesWithDistance.filter(p => FOOD.includes(p.category)).slice(0, 10), [placesWithDistance]);
  const waterPlaces = useMemo(() => placesWithDistance.filter(p => WATER.includes(p.category)).slice(0, 10), [placesWithDistance]);
  const otherPlaces = useMemo(() => placesWithDistance.filter(p => !FOOD.includes(p.category) && !WATER.includes(p.category)).slice(0, 10), [placesWithDistance]);

  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : language === 'en' ? 'en-US' : 'es-ES', {
      day: 'numeric',
      month: 'short'
    });
  };

  const heroText = {
    es: { title: '¡Vive la isla!', subtitle: 'La guía local de Tenerife para los viajeros más curiosos y aventureros', cta: 'DESCUBRE LA ISLA' },
    en: { title: 'Live the island!', subtitle: 'The local guide to Tenerife for the most curious and adventurous travelers', cta: 'DISCOVER THE ISLAND' },
    de: { title: 'Erlebe die Insel!', subtitle: 'Der lokale Reiseführer für Teneriffa für die neugierigsten und abenteuerlustigsten Reisenden', cta: 'ENTDECKE DIE INSEL' },
  };

  const currentHero = heroText[language] || heroText.es;

  // Ilustración 3D de Tenerife - imagen completa con fondo integrado
  const tenerife3DImage = "https://customer-assets.emergentagent.com/job_8edaf2f9-2828-424e-8e83-794d05150445/artifacts/h2no4gjv_Gemini_Generated_Image_9613519613519613.jpeg";

  const homeFaqs = language === 'en' ? [
    { q: "What is Calma Guide Tenerife?", a: "Calma Guide is the most complete free tourism guide for Tenerife. It includes 224 hiking trails, beaches, restaurants, live webcams, weather forecasts, and a cultural events calendar." },
    { q: "How many hiking trails are in Tenerife?", a: "Tenerife has 224 official trails: 183 hiking trails, 17 bike/horse routes, and 24 vehicle tracks. Calma Guide shows them all with maps, elevation profiles, and real-time alerts." },
    { q: "Where can I check the weather in Tenerife right now?", a: "Calma Guide offers live webcams from 6 zones of Tenerife (Santa Cruz, Los Gigantes, El Medano, Teide, Puerto de la Cruz, Las Americas) plus detailed weather forecasts from Windy." },
  ] : language === 'de' ? [
    { q: "Was ist Calma Guide Tenerife?", a: "Calma Guide ist der umfassendste kostenlose Reisefuhrer fur Teneriffa. Er umfasst 224 Wanderwege, Strande, Restaurants, Live-Webcams, Wettervorhersagen und einen Kulturveranstaltungskalender." },
    { q: "Wie viele Wanderwege gibt es auf Teneriffa?", a: "Teneriffa hat 224 offizielle Wege: 183 Wanderwege, 17 Rad-/Reitwege und 24 Fahrzeugpisten. Calma Guide zeigt sie alle mit Karten, Hohenprofilen und Echtzeit-Warnungen." },
    { q: "Wo kann ich das Wetter auf Teneriffa jetzt prufen?", a: "Calma Guide bietet Live-Webcams aus 6 Zonen Teneriffas plus detaillierte Wettervorhersagen von Windy." },
  ] : [
    { q: "Que es Calma Guide Tenerife?", a: "Calma Guide es la guia turistica gratuita mas completa de Tenerife. Incluye 224 senderos, playas, restaurantes, webcams en vivo, prevision del tiempo y agenda cultural." },
    { q: "Cuantos senderos hay en Tenerife?", a: "Tenerife tiene 224 senderos oficiales: 183 a pie, 17 en bici/caballo y 24 para vehiculos. Calma Guide los muestra todos con mapas, perfiles de alturas y alertas en tiempo real." },
    { q: "Donde puedo ver el tiempo en Tenerife ahora mismo?", a: "Calma Guide ofrece webcams en directo de 6 zonas de Tenerife (Santa Cruz, Los Gigantes, El Medano, Teide, Puerto de la Cruz, Las Americas) mas previsiones detalladas de Windy." },
  ];

  const seoDesc = language === 'en' 
    ? 'The most complete free Tenerife travel guide. 224 hiking trails, beaches, live webcams, weather, events and more. In English, Spanish and German.'
    : language === 'de'
    ? 'Der umfassendste kostenlose Reisefuhrer fur Teneriffa. 224 Wanderwege, Strande, Live-Webcams, Wetter, Veranstaltungen und mehr.'
    : 'La guia turistica mas completa y gratuita de Tenerife. 224 senderos, playas, webcams en directo, tiempo, eventos y mucho mas. En espanol, ingles y aleman.';

  return (
    <div className="pb-20" data-testid="home-page">
      <SEO 
        title={null}
        description={seoDesc}
        path="/"
        lang={language}
        schema={[websiteSchema(), faqSchema(homeFaqs)]}
      />
      <AlertBand type="movilidad" compact className="px-4 pt-3" />
      {/* Hero Section - Con imagen de fondo completa */}
      <section 
        className="relative min-h-[90vh] flex flex-col overflow-hidden"
        style={{
          backgroundImage: `url(${tenerife3DImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center bottom',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay sutil para mejorar legibilidad del texto */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#005FA3]/40 via-transparent to-transparent" />
        
        {/* Content at top */}
        <div className="relative z-10 text-center px-6 pt-12 pb-4">
          <h1 className="text-4xl font-bold text-white mb-3 drop-shadow-lg">
            {currentHero.title}
          </h1>
          <p className="text-base text-white mb-6 max-w-xs mx-auto leading-relaxed drop-shadow-md">
            {language === 'es' 
              ? 'Explora esta especial guía de Tenerife con todos nuestros lugares favoritos.'
              : language === 'de'
              ? 'Entdecke diesen besonderen Reiseführer für Teneriffa mit all unseren Lieblingsorten.'
              : 'Explore this special guide to Tenerife with all our favorite places.'}
          </p>
          <button
            onClick={() => navigate('/explore')}
            className="bg-[#38B2AC] hover:bg-[#319795] text-white font-semibold uppercase tracking-wider px-10 py-4 rounded-full text-sm shadow-lg transition-all active:scale-95"
            data-testid="explore-btn"
          >
            {currentHero.cta}
          </button>
          <div className="mt-4">
            <ShareButton kind="app" variant="text" className="text-white/90 hover:text-white drop-shadow" testId="home-share-app" />
          </div>
        </div>
        
        {/* Spacer to push content up and show more of the island */}
        <div className="flex-1" />
      </section>

      {/* Restaurants & Gastronomy Nearby */}
      <section className="py-6">
        <div className="flex items-center justify-between mb-4 px-4">
          <h2 className="section-title">
            {language === 'es' ? 'DONDE COMER CERCA DE TI' : language === 'de' ? 'WO IN DEINER NÄHE ESSEN' : 'WHERE TO EAT NEAR YOU'}
          </h2>
          <Link to="/explore?category=restaurants" className="text-[#38B2AC] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">
            {t('seeAll')} <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">{[...Array(4)].map((_, i) => <Skeleton key={i} className="w-44 h-48 flex-shrink-0" />)}</div>
        ) : (
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide snap-x snap-mandatory">
            {foodPlaces.map((place) => (
              <div key={place.id} className="flex-shrink-0 w-44 snap-start" onClick={() => navigate(`/place/${place.id}`)}>
                <div className="card-flat cursor-pointer group">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img loading="lazy" src={place.images?.[0] || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400'} alt={getLocalizedField(place, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {place._distance !== undefined && (
                      <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium text-gray-700">
                        {fmtDist(place._distance)}
                      </div>
                    )}
                  </div>
                  <div className="py-2">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-1">{getLocalizedField(place, 'name')}</h3>
                    <p className="text-xs text-gray-500 line-clamp-1">{place.address || 'Tenerife'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Beaches & Natural Pools Nearby */}
      <section className="py-6 bg-gray-50/50">
        <div className="flex items-center justify-between mb-4 px-4">
          <h2 className="section-title">
            {language === 'es' ? 'PLAYAS Y CHARCOS CERCA DE TI' : language === 'de' ? 'STRÄNDE UND POOLS IN DEINER NÄHE' : 'BEACHES & POOLS NEAR YOU'}
          </h2>
          <Link to="/explore?category=beaches" className="text-[#38B2AC] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all">
            {t('seeAll')} <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">{[...Array(4)].map((_, i) => <Skeleton key={i} className="w-44 h-48 flex-shrink-0" />)}</div>
        ) : (
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide snap-x snap-mandatory">
            {waterPlaces.map((place) => (
              <div key={place.id} className="flex-shrink-0 w-44 snap-start" onClick={() => navigate(`/place/${place.id}`)}>
                <div className="card-flat cursor-pointer group">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img loading="lazy" src={place.images?.[0] || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400'} alt={getLocalizedField(place, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {place._distance !== undefined && (
                      <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium text-gray-700">
                        {fmtDist(place._distance)}
                      </div>
                    )}
                  </div>
                  <div className="py-2">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-1">{getLocalizedField(place, 'name')}</h3>
                    <p className="text-xs text-gray-500 line-clamp-1">{place.address || 'Tenerife'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Interesting Places Nearby */}
      <section className="py-6">
        <div className="flex items-center justify-between mb-4 px-4">
          <h2 className="section-title">
            {language === 'es' ? 'SITIOS INTERESANTES CERCA DE TI' : language === 'de' ? 'INTERESSANTE ORTE IN DEINER NÄHE' : 'INTERESTING PLACES NEAR YOU'}
          </h2>
          <Link to="/explore" className="text-[#38B2AC] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all" data-testid="see-all-nearby">
            {t('seeAll')} <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">{[...Array(4)].map((_, i) => <Skeleton key={i} className="w-44 h-48 flex-shrink-0" />)}</div>
        ) : (
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide snap-x snap-mandatory">
            {otherPlaces.map((place) => (
              <div key={place.id} className="flex-shrink-0 w-44 snap-start" onClick={() => navigate(`/place/${place.id}`)}>
                <div className="card-flat cursor-pointer group">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img loading="lazy" src={place.images?.[0] || 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400'} alt={getLocalizedField(place, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {place._distance !== undefined && (
                      <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium text-gray-700">
                        {fmtDist(place._distance)}
                      </div>
                    )}
                  </div>
                  <div className="py-2">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-1">{getLocalizedField(place, 'name')}</h3>
                    <p className="text-xs text-gray-500 line-clamp-1">{place.address || 'Tenerife'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Itineraries Section */}
      {isSectionVisible('itineraries') && itineraries.length > 0 && (
        <section className="px-4 py-8 max-w-7xl mx-auto bg-gray-50">
          <div className="flex items-center justify-between mb-6">
            <h2 className="section-title">
              {language === 'es' ? 'ITINERARIOS RECOMENDADOS' : language === 'de' ? 'EMPFOHLENE ROUTEN' : 'RECOMMENDED ITINERARIES'}
            </h2>
            <Link
              to="/itineraries"
              className="text-[#38B2AC] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
              data-testid="see-all-itineraries"
            >
              {t('seeAll')} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="space-y-3">
            {itineraries.map((itinerary) => (
              <div
                key={itinerary.id}
                onClick={() => navigate(`/itinerary/${itinerary.id}`)}
                className="card-flat flex items-center gap-4 p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                data-testid={`itinerary-preview-${itinerary.id}`}
              >
                <img loading="lazy"
                  src={itinerary.image}
                  alt={getLocalizedField(itinerary, 'name')}
                  className="w-20 h-20 object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">
                    {getLocalizedField(itinerary, 'name')}
                  </h3>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2">
                    {getLocalizedField(itinerary, 'description')}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {itinerary.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={12} />
                      {itinerary.distance_km} km
                    </span>
                    <span>{itinerary.stops?.length || 0} paradas</span>
                  </div>
                </div>
                <ArrowRight size={16} className="text-gray-300 flex-shrink-0" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section className="py-8">
          <div className="flex items-center justify-between mb-4 px-4">
            <h2 className="section-title">
              {language === 'es' ? 'PROXIMOS EVENTOS' : language === 'de' ? 'KOMMENDE EVENTS' : 'UPCOMING EVENTS'}
            </h2>
            <Link
              to="/events"
              className="text-[#38B2AC] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
              data-testid="see-all-events"
            >
              {t('seeAll')} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide snap-x snap-mandatory">
            {upcomingEvents.map((event) => {
              const title = event[`title_${language}`] || event.title_es;
              return (
                <div
                  key={event.id}
                  onClick={() => navigate('/events')}
                  className="flex-shrink-0 w-72 snap-start cursor-pointer group"
                  data-testid={`event-preview-${event.id}`}
                >
                  <div className="relative overflow-hidden bg-white shadow-md border border-gray-100">
                    {/* Event image */}
                    <div className="relative h-36 overflow-hidden">
                      {event.image ? (
                        <img loading="lazy"
                          src={event.image}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#38B2AC] to-[#319795] flex items-center justify-center">
                          <Calendar size={40} className="text-white/50" />
                        </div>
                      )}
                      {/* Date badge */}
                      <div className="absolute top-3 left-3 bg-white shadow-md px-2 py-1 text-center">
                        <div className="text-xs font-bold text-[#38B2AC] uppercase">
                          {new Date(event.event_date).toLocaleDateString(language === 'de' ? 'de-DE' : language === 'en' ? 'en-US' : 'es-ES', { month: 'short' })}
                        </div>
                        <div className="text-lg font-bold text-gray-900 leading-none">
                          {new Date(event.event_date).getDate()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Event info */}
                    <div className="p-3">
                      <h3 className="font-semibold text-gray-900 text-sm line-clamp-1 mb-1">
                        {title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {event.event_time && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {event.event_time.slice(0, 5)}
                          </span>
                        )}
                        {event.venue_name && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin size={12} />
                            {event.venue_name}
                          </span>
                        )}
                      </div>
                      {event.ticket_url && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-[#38B2AC] font-medium">
                          <Ticket size={12} />
                          {language === 'es' ? 'Entradas disponibles' : language === 'de' ? 'Tickets verfugbar' : 'Tickets available'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Categories Section - Vertical format carousel */}
      <section className="py-8">
        <h2 className="section-title mb-4 px-4">
          {language === 'es' ? 'EXPLORA POR CATEGORÍA' : language === 'de' ? 'NACH KATEGORIE ERKUNDEN' : 'EXPLORE BY CATEGORY'}
        </h2>

        {loading ? (
          <div className="flex gap-3 overflow-x-auto px-4 scrollbar-hide">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="w-32 h-48 flex-shrink-0" />
            ))}
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide snap-x snap-mandatory">
            {categories.map((category) => (
              <div
                key={category.id}
                onClick={() => navigate(`/explore?category=${category.slug}`)}
                className="relative flex-shrink-0 w-32 aspect-[3/4] overflow-hidden cursor-pointer group snap-start"
                data-testid={`category-card-${category.slug}`}
              >
                <img loading="lazy"
                  src={category.image}
                  alt={getLocalizedField(category, 'name')}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <h3 className="text-white font-medium text-xs">
                    {getLocalizedField(category, 'name')}
                  </h3>
                  {getLocalizedField(category, 'subtitle') && (
                    <p className="text-white/80 text-[10px] leading-tight mt-0.5 line-clamp-2" data-testid={`category-subtitle-${category.slug}`}>
                      {getLocalizedField(category, 'subtitle')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default HomePage;
